import React, { useState, useEffect } from 'react';
import { 
  Scale, PieChart, TrendingUp, TrendingDown, RefreshCw, Loader,
  AlertCircle, Check, X, Plus, Trash2, Target, ArrowRight,
  LogIn, Wallet, AlertTriangle, Info, User
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'];

// Sample data for non-logged in users
const SAMPLE_HOLDINGS = [
  { symbol: 'VTI', shares: 50, purchase_price: 220, name: 'Vanguard Total Stock Market ETF' },
  { symbol: 'VXUS', shares: 30, purchase_price: 55, name: 'Vanguard Total International Stock ETF' },
  { symbol: 'BND', shares: 40, purchase_price: 75, name: 'Vanguard Total Bond Market ETF' },
  { symbol: 'VNQ', shares: 15, purchase_price: 85, name: 'Vanguard Real Estate ETF' }
];

const SAMPLE_TARGETS = [
  { symbol: 'VTI', allocation: 40 },
  { symbol: 'VXUS', allocation: 20 },
  { symbol: 'BND', allocation: 30 },
  { symbol: 'VNQ', allocation: 10 }
];

const PortfolioRebalancer = ({ user, livePositions, isLiveTrading }) => {
  const [holdings, setHoldings] = useState([]);
  const [targetAllocations, setTargetAllocations] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [newHolding, setNewHolding] = useState({ symbol: '', shares: '', purchase_price: '' });
  const [userPortfolios, setUserPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [isUsingUserData, setIsUsingUserData] = useState(false);

  // Load user portfolio or sample data
  useEffect(() => {
    // If live Alpaca positions passed directly from TradingDashboard, use them
    if (livePositions && livePositions.length > 0) {
      const totalValue = livePositions.reduce((sum, p) => sum + (p.market_value || p.marketValue || 0), 0);
      const h = livePositions.map(p => ({
        symbol: p.symbol,
        shares: parseFloat(p.shares || p.quantity || 0),
        currentPrice: parseFloat(p.current_price || p.currentPrice || 0),
        value: parseFloat(p.market_value || p.marketValue || 0),
        source: 'alpaca'
      }));
      const targets = h.map(holding => ({
        symbol: holding.symbol,
        allocation: parseFloat(((holding.value / totalValue) * 100).toFixed(2))
      }));
      setHoldings(h);
      setTargetAllocations(targets);
      setIsUsingUserData(true);
      console.log('✅ Rebalancer loaded', h.length, 'positions from livePositions prop');
    } else if (user) {
      fetchUserPortfolios();
    } else {
      // Use sample data for non-logged in users
      setHoldings(SAMPLE_HOLDINGS);
      setTargetAllocations(SAMPLE_TARGETS);
      setIsUsingUserData(false);
    }
    fetchTemplates();
  }, [user, livePositions]);

  const fetchUserPortfolios = async () => {
    if (!user) return;
    
    setPortfolioLoading(true);
    try {
      const token = localStorage.getItem('investorq_token');

      // ── PRIORITY 1: Load live Alpaca positions ──
      const alpacaRes = await fetch(`${API_URL}/trading/positions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-id': user.id.toString()
        }
      });

      if (alpacaRes.ok) {
        const alpacaData = await alpacaRes.json();
        const positions = alpacaData.positions || alpacaData || [];
        if (Array.isArray(positions) && positions.length > 0) {
          const totalValue = positions.reduce((sum, p) => sum + parseFloat(p.market_value || 0), 0);
          const holdings = positions.map(p => ({
            symbol: p.symbol,
            shares: parseFloat(p.qty),
            currentPrice: parseFloat(p.current_price || p.lastday_price || 0),
            value: parseFloat(p.market_value || 0),
            source: 'alpaca'
          }));
          const targets = holdings.map(h => ({
            symbol: h.symbol,
            allocation: parseFloat(((h.value / totalValue) * 100).toFixed(2))
          }));
          setHoldings(holdings);
          setTargetAllocations(targets);
          setIsUsingUserData(true);
          console.log('✅ Loaded', holdings.length, 'live Alpaca positions into rebalancer');
          setPortfolioLoading(false);
          return;
        }
      }

      // ── PRIORITY 2: Fall back to portfolio DB ──
      const response = await fetch(`${API_URL}/portfolio`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'x-user-id': user.id.toString()
        }
      });
      
      if (response.ok) {
        const portfolios = await response.json();
        console.log('📊 Fetched portfolios:', portfolios);
        setUserPortfolios(Array.isArray(portfolios) ? portfolios : [portfolios]);
        
        const portfolioArray = Array.isArray(portfolios) ? portfolios : [portfolios];
        if (portfolioArray.length > 0) {
          // Auto-select first portfolio
          selectPortfolio(portfolioArray[0]);
        } else {
          // User has no portfolios yet
          setHoldings([]);
          setTargetAllocations([]);
          setIsUsingUserData(true);
        }
      } else {
        console.log('❌ Failed to fetch portfolios, status:', response.status);
        // Fall back to sample data
        setHoldings(SAMPLE_HOLDINGS);
        setTargetAllocations(SAMPLE_TARGETS);
        setIsUsingUserData(false);
      }
    } catch (err) {
      console.error('Failed to fetch portfolios:', err);
      // Fall back to sample data
      setHoldings(SAMPLE_HOLDINGS);
      setTargetAllocations(SAMPLE_TARGETS);
      setIsUsingUserData(false);
    } finally {
      setPortfolioLoading(false);
    }
  };

  const selectPortfolio = async (portfolio) => {
    if (!user) return;
    
    setSelectedPortfolio(portfolio);
    setPortfolioLoading(true);
    
    try {
      const token = localStorage.getItem('investorq_token');
      
      // Use the correct endpoint: /api/portfolio/:portfolioId
      const response = await fetch(`${API_URL}/portfolio/${portfolio.id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'x-user-id': user.id.toString()
        }
      });
      
      console.log('📊 Portfolio details response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Portfolio details:', data);
        const portfolioHoldings = data.holdings || [];
        
        if (portfolioHoldings.length > 0) {
          setHoldings(portfolioHoldings.map(h => ({
            symbol: h.symbol,
            shares: parseFloat(h.shares) || parseFloat(h.total_shares) || 0,
            purchase_price: parseFloat(h.purchase_price) || parseFloat(h.average_cost) || parseFloat(h.avg_cost) || 100,
            name: h.name || h.symbol
          })));
          
          // Set equal weight target allocations initially
          const equalAlloc = 100 / portfolioHoldings.length;
          setTargetAllocations(portfolioHoldings.map(h => ({
            symbol: h.symbol,
            allocation: parseFloat(equalAlloc.toFixed(2))
          })));
          
          setIsUsingUserData(true);
          console.log('✅ Loaded', portfolioHoldings.length, 'holdings from user portfolio');
        } else {
          console.log('⚠️ Portfolio has no holdings');
          setHoldings([]);
          setTargetAllocations([]);
          setIsUsingUserData(true);
        }
      } else {
        console.log('❌ Failed to fetch portfolio details');
      }
    } catch (err) {
      console.error('Failed to fetch portfolio details:', err);
    } finally {
      setPortfolioLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/rebalancer/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const analyzePortfolio = async () => {
    const totalAllocation = targetAllocations.reduce((sum, t) => sum + t.allocation, 0);
    if (Math.abs(totalAllocation - 100) > 0.1) {
      setError(`Target allocations must sum to 100%. Current sum: ${totalAllocation.toFixed(1)}%`);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/rebalancer/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holdings, targetAllocations })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Analysis failed');
      }
      
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addHolding = () => {
    if (!newHolding.symbol || !newHolding.shares) return;
    
    const symbol = newHolding.symbol.toUpperCase();
    
    setHoldings([...holdings, {
      symbol,
      shares: parseFloat(newHolding.shares),
      purchase_price: parseFloat(newHolding.purchase_price) || 100
    }]);
    
    const currentTotal = targetAllocations.reduce((sum, t) => sum + t.allocation, 0);
    const newAllocation = currentTotal >= 100 ? 0 : Math.min(10, 100 - currentTotal);
    setTargetAllocations([...targetAllocations, { symbol, allocation: newAllocation }]);
    
    setNewHolding({ symbol: '', shares: '', purchase_price: '' });
    setShowAddHolding(false);
  };

  const removeHolding = (symbol) => {
    setHoldings(holdings.filter(h => h.symbol !== symbol));
    setTargetAllocations(targetAllocations.filter(t => t.symbol !== symbol));
    setAnalysis(null);
  };

  const updateTargetAllocation = (symbol, value) => {
    setTargetAllocations(targetAllocations.map(t => 
      t.symbol === symbol ? { ...t, allocation: parseFloat(value) || 0 } : t
    ));
  };

  const equalizeAllocations = () => {
    const equalAlloc = 100 / holdings.length;
    setTargetAllocations(holdings.map(h => ({ symbol: h.symbol, allocation: parseFloat(equalAlloc.toFixed(2)) })));
  };

  const loadSampleData = () => {
    setHoldings(SAMPLE_HOLDINGS);
    setTargetAllocations(SAMPLE_TARGETS);
    setIsUsingUserData(false);
    setAnalysis(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#60a5fa';
      default: return '#94a3b8';
    }
  };

  const totalTargetAllocation = parseFloat(
    (targetAllocations.reduce((sum, t) => sum + (parseFloat(t.allocation) || 0), 0)).toFixed(2)
  );

  // Show login prompt for non-logged in users
  const LoginPrompt = () => (
    <div style={{
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      borderRadius: '0.75rem',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      textAlign: 'center'
    }}>
      <LogIn size={32} color="#8b5cf6" style={{ margin: '0 auto 0.75rem' }} />
      <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
        Log In to Analyze Your Portfolio
      </h3>
      <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
        You're viewing sample data. Log in and add holdings to your portfolio to get personalized rebalancing recommendations.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{
          padding: '0.5rem 1rem',
          background: 'rgba(139, 92, 246, 0.2)',
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          color: '#c4b5fd'
        }}>
          📊 Sample Portfolio Data
        </div>
      </div>
    </div>
  );

  // Empty portfolio prompt
  const EmptyPortfolioPrompt = () => (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: '1px solid #334155',
      borderRadius: '0.75rem',
      padding: '2rem',
      textAlign: 'center',
      marginBottom: '1.5rem'
    }}>
      <Wallet size={48} color="#64748b" style={{ margin: '0 auto 1rem' }} />
      <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
        No Holdings Found
      </h3>
      <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
        Your portfolio doesn't have any holdings yet. Add stocks to your portfolio first, or use sample data to explore the rebalancer.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={loadSampleData}
          style={{
            padding: '0.625rem 1.25rem',
            background: 'rgba(139, 92, 246, 0.2)',
            border: '1px solid rgba(139, 92, 246, 0.5)',
            borderRadius: '0.5rem',
            color: '#c4b5fd',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}
        >
          Load Sample Data
        </button>
        <button
          onClick={() => setShowAddHolding(true)}
          style={{
            padding: '0.625rem 1.25rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem'
          }}
        >
          <Plus size={16} />
          Add Holding
        </button>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', 
        borderRadius: '0.75rem', 
        padding: '1.25rem', 
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Scale size={24} />
              Portfolio Rebalancer
            </h2>
            <p style={{ color: '#ddd6fe', fontSize: '0.75rem', margin: 0 }}>
              {user ? (isUsingUserData ? 'Analyzing your portfolio' : 'Using sample data') : 'Sample data - Log in for personalized analysis'}
            </p>
          </div>
          <button
            onClick={analyzePortfolio}
            disabled={loading || holdings.length === 0}
            style={{
              padding: '0.5rem 1rem',
              background: loading ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.95)',
              border: 'none',
              borderRadius: '0.5rem',
              color: '#6d28d9',
              cursor: loading || holdings.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            {loading ? <Loader size={16} className="spin" /> : <Target size={16} />}
            Analyze
          </button>
        </div>
      </div>

      {/* User Status Banner */}
      {user && isUsingUserData && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '0.5rem',
          padding: '0.75rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.8rem'
        }}>
          <User size={16} color="#34d399" />
          <span style={{ color: '#34d399' }}>
            Analyzing your portfolio: <strong>{selectedPortfolio?.name || 'My Portfolio'}</strong>
          </span>
          {userPortfolios.length > 1 && (
            <select
              value={selectedPortfolio?.id || ''}
              onChange={(e) => {
                const portfolio = userPortfolios.find(p => p.id === parseInt(e.target.value));
                if (portfolio) selectPortfolio(portfolio);
              }}
              style={{
                marginLeft: 'auto',
                padding: '0.25rem 0.5rem',
                background: '#1e293b',
                border: '1px solid #475569',
                borderRadius: '0.375rem',
                color: 'white',
                fontSize: '0.75rem'
              }}
            >
              {userPortfolios.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Login Prompt for non-logged in users */}
      {!user && <LoginPrompt />}

      {/* Loading State */}
      {portfolioLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size={32} color="#8b5cf6" className="spin" />
        </div>
      )}

      {/* Empty Portfolio Prompt */}
      {user && !portfolioLoading && holdings.length === 0 && <EmptyPortfolioPrompt />}

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.5rem',
          padding: '0.75rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#f87171',
          fontSize: '0.875rem'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Main Content - Only show if we have holdings */}
      {holdings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          {/* Holdings & Target Allocations */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Wallet size={18} color="#8b5cf6" />
                Holdings & Target Allocations
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={equalizeAllocations}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '0.375rem',
                    color: '#c4b5fd',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '600'
                  }}
                >
                  Equal Weight
                </button>
                <button
                  onClick={() => setShowAddHolding(true)}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '0.375rem',
                    color: '#34d399',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Plus size={12} />
                  Add
                </button>
              </div>
            </div>

            {/* Holdings List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {holdings.map((holding, idx) => {
                const target = targetAllocations.find(t => t.symbol === holding.symbol);
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      background: 'rgba(51, 65, 85, 0.3)',
                      borderRadius: '0.5rem'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{holding.symbol}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        {holding.shares} shares @ ${(holding.currentPrice || holding.purchase_price || 0).toFixed(2)}
                        {holding.value > 0 && <span style={{ color: '#64748b' }}> · ${holding.value.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Target:</span>
                      <input
                        type="number"
                        value={target?.allocation || 0}
                        onChange={(e) => updateTargetAllocation(holding.symbol, e.target.value)}
                        style={{
                          width: '50px',
                          padding: '0.25rem 0.375rem',
                          background: '#1e293b',
                          border: '1px solid #475569',
                          borderRadius: '0.25rem',
                          color: 'white',
                          fontSize: '0.8rem',
                          textAlign: 'center'
                        }}
                      />
                      <span style={{ fontSize: '0.8rem' }}>%</span>
                      <button
                        onClick={() => removeHolding(holding.symbol)}
                        style={{
                          padding: '0.25rem',
                          background: 'transparent',
                          border: 'none',
                          color: '#f87171',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total Allocation */}
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: totalTargetAllocation === 100 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${totalTargetAllocation === 100 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              borderRadius: '0.375rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.8rem'
            }}>
              <span>Total Target Allocation:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: totalTargetAllocation === 100 ? '#34d399' : '#f87171'
              }}>
                {totalTargetAllocation.toFixed(1)}%
                {totalTargetAllocation === 100 ? ' ✓' : ' (must equal 100%)'}
              </span>
            </div>
          </div>

          {/* Add Holding Modal */}
          {showAddHolding && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.95)',
              border: '1px solid #475569',
              borderRadius: '0.75rem',
              padding: '1rem'
            }}>
              <h4 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>Add New Holding</h4>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Symbol (e.g., AAPL)"
                  value={newHolding.symbol}
                  onChange={(e) => setNewHolding({ ...newHolding, symbol: e.target.value.toUpperCase() })}
                  style={{
                    flex: 1,
                    minWidth: '80px',
                    padding: '0.5rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.375rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                />
                <input
                  type="number"
                  placeholder="Shares"
                  value={newHolding.shares}
                  onChange={(e) => setNewHolding({ ...newHolding, shares: e.target.value })}
                  style={{
                    width: '70px',
                    padding: '0.5rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.375rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                />
                <input
                  type="number"
                  placeholder="Avg Cost"
                  value={newHolding.purchase_price}
                  onChange={(e) => setNewHolding({ ...newHolding, purchase_price: e.target.value })}
                  style={{
                    width: '80px',
                    padding: '0.5rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.375rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                />
                <button onClick={addHolding} style={{
                  padding: '0.5rem 0.75rem',
                  background: '#8b5cf6',
                  border: 'none',
                  borderRadius: '0.375rem',
                  color: 'white',
                  cursor: 'pointer'
                }}>
                  <Check size={16} />
                </button>
                <button onClick={() => setShowAddHolding(false)} style={{
                  padding: '0.5rem 0.75rem',
                  background: '#475569',
                  border: 'none',
                  borderRadius: '0.375rem',
                  color: 'white',
                  cursor: 'pointer'
                }}>
                  <X size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {analysis && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '1rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PieChart size={18} color="#8b5cf6" />
                Rebalance Analysis
              </h3>

              {/* Portfolio Value & Drift */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ background: 'rgba(139, 92, 246, 0.1)', padding: '0.75rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Total Value</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#c4b5fd' }}>
                    ${(analysis.summary?.totalValue || analysis.totalValue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div style={{ 
                  background: (analysis.summary?.totalDrift || analysis.totalDrift || 0) > 10 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)', 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem', 
                  textAlign: 'center' 
                }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Portfolio Drift</div>
                  <div style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: 'bold', 
                    color: (analysis.summary?.totalDrift || analysis.totalDrift || 0) > 10 ? '#f87171' : '#34d399' 
                  }}>
                    {(analysis.summary?.totalDrift || analysis.totalDrift || 0).toFixed(1)}%
                  </div>
                </div>
                <div style={{ 
                  background: (analysis.summary?.rebalanceUrgency || analysis.urgency) === 'high' ? 'rgba(239, 68, 68, 0.1)' : 
                              (analysis.summary?.rebalanceUrgency || analysis.urgency) === 'medium' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(96, 165, 250, 0.1)', 
                  padding: '0.75rem', 
                  borderRadius: '0.5rem', 
                  textAlign: 'center' 
                }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Urgency</div>
                  <div style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: 'bold', 
                    color: (analysis.summary?.rebalanceUrgency || analysis.urgency) === 'high' ? '#f87171' : 
                           (analysis.summary?.rebalanceUrgency || analysis.urgency) === 'medium' ? '#fbbf24' : '#60a5fa',
                    textTransform: 'capitalize'
                  }}>
                    {analysis.summary?.rebalanceUrgency || analysis.urgency || 'low'}
                  </div>
                </div>
              </div>

              {/* Pie Charts */}
              {analysis.positions && analysis.positions.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
                    Current vs Target Allocation
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem', textAlign: 'center' }}>Current</div>
                      <div style={{ height: '140px', minHeight: '140px', minWidth: '100px' }}>
                        <ResponsiveContainer width="100%" height={140}>
                          <RechartsPie>
                            <Pie
                              data={analysis.positions.map((p, i) => ({ name: p.symbol, value: p.actualAllocation }))}
                              dataKey="value"
                              cx="50%"
                              cy="50%"
                              outerRadius={50}
                              label={({ name, value }) => `${name}: ${value.toFixed(0)}%`}
                              labelLine={false}
                            >
                              {analysis.positions.map((_, idx) => (
                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.25rem', textAlign: 'center' }}>Target</div>
                      <div style={{ height: '140px', minHeight: '140px', minWidth: '100px' }}>
                        <ResponsiveContainer width="100%" height={140}>
                          <RechartsPie>
                            <Pie
                              data={analysis.positions.map((p, i) => ({ name: p.symbol, value: p.targetAllocation }))}
                              dataKey="value"
                              cx="50%"
                              cy="50%"
                              outerRadius={50}
                              label={({ name, value }) => `${name}: ${value.toFixed(0)}%`}
                              labelLine={false}
                            >
                              {analysis.positions.map((_, idx) => (
                                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
                    Rebalancing Actions
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {analysis.recommendations.map((rec, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem',
                          background: rec.action === 'BUY' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          border: `1px solid ${rec.action === 'BUY' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                          borderRadius: '0.5rem'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            padding: '0.125rem 0.375rem',
                            background: rec.action === 'BUY' ? '#34d399' : '#f87171',
                            borderRadius: '0.25rem',
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            color: 'white'
                          }}>
                            {rec.action}
                          </span>
                          <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{rec.symbol}</span>
                          <span style={{
                            padding: '0.0625rem 0.25rem',
                            background: getPriorityColor(rec.priority),
                            borderRadius: '0.25rem',
                            fontSize: '0.55rem',
                            fontWeight: '700',
                            color: 'white',
                            textTransform: 'uppercase'
                          }}>
                            {rec.priority}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '0.8rem' }}>
                          <div style={{ fontWeight: '600' }}>
                            {(rec.sharesToTrade || rec.shares || 0).toFixed(2)} shares
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                            ~${(rec.dollarAmount || rec.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Allocation Templates */}
          {templates.length > 0 && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '1rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>
                📋 Allocation Templates
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                {templates.map((template, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      background: selectedTemplate?.name === template.name ? 'rgba(139, 92, 246, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                      border: selectedTemplate?.name === template.name ? '1px solid rgba(139, 92, 246, 0.5)' : '1px solid #475569',
                      borderRadius: '0.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.25rem' }}>{template.name}</div>
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{template.description}</div>
                    <div style={{ 
                      marginTop: '0.375rem',
                      fontSize: '0.6rem',
                      padding: '0.125rem 0.375rem',
                      background: template.riskLevel === 'high' ? 'rgba(239, 68, 68, 0.2)' : 
                                  template.riskLevel === 'medium' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                      borderRadius: '0.25rem',
                      display: 'inline-block',
                      color: template.riskLevel === 'high' ? '#f87171' : 
                             template.riskLevel === 'medium' ? '#fbbf24' : '#34d399'
                    }}>
                      {template.riskLevel} risk
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Educational Disclaimer */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
          <AlertTriangle size={20} color="#fbbf24" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#fbbf24', margin: '0 0 0.5rem 0' }}>
              Educational Purposes Only
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.5', margin: 0 }}>
              This portfolio rebalancer tool is provided for <strong>educational and informational purposes only</strong>. 
              The analysis, recommendations, and suggestions provided are not financial advice. 
              Before making any investment decisions, please:
            </p>
            <ul style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem', paddingLeft: '1.25rem', marginBottom: 0 }}>
              <li>Consult with a qualified financial advisor</li>
              <li>Conduct your own research and due diligence</li>
              <li>Consider your personal financial situation and risk tolerance</li>
              <li>Understand that past performance does not guarantee future results</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: 'rgba(139, 92, 246, 0.1)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
          <Info size={16} color="#8b5cf6" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
            <strong style={{ color: '#c4b5fd' }}>How it works:</strong> Enter your current holdings and set target allocations. 
            The rebalancer calculates the drift from your targets and suggests trades to bring your portfolio back into balance. 
            Rebalancing regularly can help maintain your desired risk profile.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioRebalancer;